import React from "react";
import { Lock } from "lucide-react";
import { Label } from "../ui/label";
import { computeTotals, earlyActiveNow } from "../../lib/calc";

export const pickPay = (d) => ({
  stato_pagamento_acconto: d.stato_pagamento_acconto,
  stato_pagamento_saldo: d.stato_pagamento_saldo || "da_pagare",
  deposit: d.deposit || {},
  balance: d.balance || {},
});

export const emptyItem = () => ({
  id: crypto.randomUUID(),
  title: "",
  description: "",
  unit_price: 0,
  unit: "corpo",
  quantity: 1,
  work_days: 0,
  work_unit: "giorni",
  item_type: "base",
  min_qty: 1,
  max_qty: 10,
  enabled_default: true,
  discount_type: "none",
  discount_value: 0,
  iva_rate: 22,
});

export const DEFAULT_VEXATIOUS = "Ai sensi degli artt. 1341 e 1342 c.c., il Cliente approva specificamente le seguenti clausole: limitazione di responsabilità del fornitore; penali in caso di ritardato pagamento; divieto di recesso ad opera avviata; Foro competente esclusivo del luogo in cui ha sede il fornitore.";

export const TYPE_META = {
  base: {
    label: "Fissa / Obbligatoria (Inclusa)",
    help: "📌 Voce fissa inclusa nel preventivo: il cliente non può rimuoverla né modificarne la quantità.",
  },
  optional: {
    label: "Opzionale (Add-on con switch Sì/No)",
    help: "✨ Upsell opzionale: il cliente vedrà un interruttore per aggiungere o meno questo servizio extra al preventivo.",
  },
  variable: {
    label: "Quantità Modificabile dal Cliente (+/-)",
    help: "🔢 Quantità flessibile: il cliente potrà aumentare o diminuire autonomamente la quantità (es. numero di pagine, ore, post) vedendo il prezzo aggiornarsi.",
  },
};

export function ProBadge({ onClick, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors">
      <Lock style={{ width: 10, height: 10 }} /> PRO
    </button>
  );
}

export function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export const qtyError = (it) => {
  if (it.item_type !== "variable") return null;
  const q = Number(it.quantity), lo = Number(it.min_qty), hi = Number(it.max_qty);
  if (lo > hi) return "La Q.tà minima non può essere superiore alla Q.tà massima.";
  if (q < lo) return `La quantità predefinita (${q}) è inferiore alla Q.tà minima (${lo}).`;
  if (q > hi) return `La quantità predefinita (${q}) supera la Q.tà massima (${hi}).`;
  return null;
};

export function Row({ label, value, bold, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${muted ? "text-slate-500" : "text-slate-700"} ${bold ? "font-bold text-slate-900" : ""}`}>{label}</span>
      <span className={`font-mono ${bold ? "text-lg font-extrabold text-slate-900" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}

export const emptyQuote = () => ({
  title: "",
  description: "",
  status: "bozza",
  client: { name: "", email: "", phone: "", vat: "", address: "", type: "business" },
  due_date: "",
  items: [],
  groups: [],
  payment_terms: "50_50",
  terms_conditions: "",
  legal_notes_custom: null,
  bollo_id: "",
  vexatious_clauses: DEFAULT_VEXATIOUS,
  global_discount_type: "none",
  global_discount_value: 0,
  currency: "EUR",
  early_discount_type: "none",
  early_discount_value: 0,
  early_discount_deadline: "",
  progress_enabled: true,
});

export const quoteFromApi = (data) => ({
  title: data.title || "",
  description: data.description || "",
  status: data.status || "bozza",
  public_token: data.public_token || "",
  accepted_name: data.accepted_name || "",
  client: data.client || { name: "", email: "", phone: "", vat: "", address: "", type: "business" },
  due_date: data.due_date || "",
  items: data.items || [],
  groups: data.groups || [],
  payment_terms: data.payment_terms || "50_50",
  terms_conditions: data.terms_conditions || "",
  legal_notes_custom: data.legal_notes_custom ?? null,
  bollo_id: data.bollo_id || "",
  vexatious_clauses: data.vexatious_clauses != null ? data.vexatious_clauses : DEFAULT_VEXATIOUS,
  global_discount_type: data.global_discount_type || "none",
  global_discount_value: data.global_discount_value || 0,
  currency: data.currency || "EUR",
  early_discount_type: data.early_discount_type || "none",
  early_discount_value: data.early_discount_value || 0,
  early_discount_deadline: data.early_discount_deadline || "",
  progress_enabled: data.progress_enabled !== false,
});

export const taxFrom = (src = {}) => ({
  tax_regime: src.tax_regime === "semplificato" ? "ordinario" : src.tax_regime || "forfettario",
  previdenza: src.previdenza || "nessuno",
  cassa_rate: Number(src.cassa_rate) || 4,
  ritenuta_acconto: !!src.ritenuta_acconto,
  addebito_bollo: !!src.addebito_bollo,
});

const REGIME_LABELS = { forfettario: "Forfettario", ordinario: "Ordinario", occasionale: "Prestazione occasionale" };

export const taxSummary = (t) => [
  REGIME_LABELS[t.tax_regime] || t.tax_regime,
  t.previdenza === "inps" ? "Rivalsa INPS 4%" : t.previdenza === "cassa" ? `Cassa Professionale ${t.cassa_rate}%` : "nessun contributo",
  t.ritenuta_acconto ? "ritenuta 20%" : "senza ritenuta",
  t.addebito_bollo ? "bollo addebitato" : "bollo non addebitato",
].join(" · ");

export const editorTotals = (q, profile) => computeTotals({
  items: q.items,
  selections: {},
  tax_regime: profile.tax_regime,
  previdenza: profile.previdenza,
  cassa_rate: profile.cassa_rate || 4,
  ritenuta_acconto: profile.ritenuta_acconto,
  addebito_bollo: profile.addebito_bollo,
  client_type: q.client.type,
  payment_terms: q.payment_terms,
  global_discount_type: q.global_discount_type,
  global_discount_value: Number(q.global_discount_value) || 0,
  early_discount_type: q.early_discount_type,
  early_discount_value: Number(q.early_discount_value) || 0,
  early_active: earlyActiveNow(q.early_discount_type, q.early_discount_deadline || null),
  groups: q.groups,
});

const ivaOr22 = (v) => (v == null || v === "" ? 22 : Number(v));

export const buildQuotePayload = (q, asDraft = false) => ({
  ...q,
  status: asDraft ? "bozza" : (q.status || "bozza"),
  global_discount_value: Number(q.global_discount_value) || 0,
  early_discount_value: Number(q.early_discount_value) || 0,
  early_discount_deadline: q.early_discount_deadline || null,
  items: q.items.map((it) => ({
    ...it,
    unit_price: Number(it.unit_price) || 0,
    quantity: Number(it.quantity) || 1,
    unit: (it.unit || "").trim() || "corpo",
    work_days: Number(it.work_days) || 0,
    work_unit: it.work_unit || "giorni",
    min_qty: Number(it.min_qty) || 1,
    max_qty: Number(it.max_qty) || 1,
    discount_value: Number(it.discount_value) || 0,
    iva_rate: ivaOr22(it.iva_rate),
  })),
  groups: q.groups.map((g) => ({
    ...g,
    packages: g.packages.map((p) => ({
      ...p,
      price: Number(p.price) || 0,
      discount_value: Number(p.discount_value) || 0,
      work_days: Number(p.work_days) || 0,
      iva_rate: ivaOr22(p.iva_rate),
      features: Array.isArray(p.features)
        ? p.features
        : String(p.features || "").split("\n").map((x) => x.trim()).filter(Boolean),
    })),
  })),
});
